import React, { useState, useEffect } from 'react';
import { extractNodeCoordinates } from '../utils/nodeDecoder';
import { useAddMapNodeMutation, useUpdateMapNodeMutation } from '../store/api/mapNodesApi';
import '../styles/AddNodeModal.css';

const AddNodeModal = ({ onClose, onNodeAdded, onMapClickRequest, editNode = null }) => {
  const isEditMode = !!editNode;
  
  const [nodeId, setNodeId] = useState('');
  const [decoding, setDecoding] = useState(false);
  const [decodedInfo, setDecodedInfo] = useState(null);
  const [error, setError] = useState('');
  const [isWaitingForMapClick, setIsWaitingForMapClick] = useState(false);
  
  // Editable fields
  const [editableNodeHash, setEditableNodeHash] = useState('');
  const [editablePublicKey, setEditablePublicKey] = useState('');
  const [editableFriendlyName, setEditableFriendlyName] = useState('');
  const [editableLatitude, setEditableLatitude] = useState('');
  const [editableLongitude, setEditableLongitude] = useState('');
  const [isRepeater, setIsRepeater] = useState(false);
  const [isRoomServer, setIsRoomServer] = useState(false);
  const [antennaHeight, setAntennaHeight] = useState('2.0');
  const [antennaGain, setAntennaGain] = useState('2.15');
  
  const [addMapNode, { isLoading: addingNode }] = useAddMapNodeMutation();
  const [updateMapNode, { isLoading: updatingNode }] = useUpdateMapNodeMutation();
  const submitting = addingNode || updatingNode;

  // Initialize fields when editing
  useEffect(() => {
    if (editNode) {
      setNodeId(editNode.nodeId || '');
      setEditableNodeHash(editNode.nodeHash || '');
      setEditablePublicKey(editNode.publicKey || '');
      setEditableFriendlyName(editNode.friendlyName || '');
      setEditableLatitude(editNode.latitude ? editNode.latitude.toString() : '');
      setEditableLongitude(editNode.longitude ? editNode.longitude.toString() : '');
      setIsRepeater(editNode.isRepeater || false);
      setIsRoomServer(editNode.isRoomServer || false);
      setAntennaHeight(editNode.antennaHeightMeters ? editNode.antennaHeightMeters.toString() : '2.0');
      setAntennaGain(editNode.antennaGainDbi ? editNode.antennaGainDbi.toString() : '2.15');
      setDecodedInfo({ decoded: true }); // Skip decode step
    }
  }, [editNode]);

  // Cleanup: clear map click callback when modal unmounts
  useEffect(() => {
    return () => {
      // Only clear on unmount, not on every render
      if (onMapClickRequest) {
        onMapClickRequest(null);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDecode = () => {
    setDecoding(true);
    setError('');
    setDecodedInfo(null);
    setIsWaitingForMapClick(false);

    try {
      const coords = extractNodeCoordinates(nodeId);
      
      if (!coords) {
        setError('Failed to decode node ID. Please check the format.');
        return;
      }

      setDecodedInfo(coords);
      setEditableNodeHash(coords.nodeHash || '');
      setEditablePublicKey(coords.publicKey || '');
      setEditableFriendlyName(coords.name || coords.nodeHash || '');
      
      if (coords.noLocation || coords.latitude === null || coords.longitude === null) {
        if (coords.partialLocation) {
          setError('Partial location data found. Please complete the coordinates or click the map to set a new location.');
          setEditableLatitude(coords.latitude ? coords.latitude.toFixed(6) : '');
          setEditableLongitude(coords.longitude ? coords.longitude.toFixed(6) : '');
        } else {
          setError('No location data found. Click the button below to set location from the map, or enter coordinates manually.');
          setEditableLatitude('');
          setEditableLongitude('');
        }
        // Don't automatically enter map click mode - let user click the button
        setIsWaitingForMapClick(false);
      } else {
        setEditableLatitude(coords.latitude.toFixed(6));
        setEditableLongitude(coords.longitude.toFixed(6));
      }
    } catch (err) {
      setError('Error decoding node ID: ' + err.message);
    } finally {
      setDecoding(false);
    }
  };

  const handleMapClick = () => {
    setIsWaitingForMapClick(true);
    if (onMapClickRequest) {
      onMapClickRequest((lat, lng) => {
        setEditableLatitude(lat.toFixed(6));
        setEditableLongitude(lng.toFixed(6));
        setIsWaitingForMapClick(false);
        setError('');
      });
    }
  };

  const handleSubmit = async () => {
    if (!decodedInfo) {
      setError('Please decode the node ID first');
      return;
    }

    const lat = parseFloat(editableLatitude);
    const lng = parseFloat(editableLongitude);

    if (isNaN(lat) || isNaN(lng)) {
      setError('Please provide valid latitude and longitude');
      return;
    }

    setError('');

    let cleanNodeId = nodeId.trim();
    if (cleanNodeId.startsWith('meshcore://')) {
      cleanNodeId = cleanNodeId.substring(11);
    }

    const nodeData = {
      nodeHash: editableNodeHash || '0x00',
      publicKey: editablePublicKey || 'UNKNOWN',
      friendlyName: editableFriendlyName || null,
      latitude: lat,
      longitude: lng,
      isRepeater: isRepeater,
      isRoomServer: isRoomServer,
      antennaHeightMeters: (isRepeater || isRoomServer) ? parseFloat(antennaHeight) : null,
      antennaGainDbi: (isRepeater || isRoomServer) ? parseFloat(antennaGain) : null
    };

    try {
      if (isEditMode) {
        await updateMapNode({
          nodeId: cleanNodeId,
          ...nodeData
        }).unwrap();
      } else {
        await addMapNode({
          nodeId: cleanNodeId,
          ...nodeData
        }).unwrap();
      }
      
      onNodeAdded();
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} node:`, err);
      const errorMsg = err?.data?.message || err?.data?.title || err?.message || `Failed to ${isEditMode ? 'update' : 'add'} node`;
      setError(errorMsg);
    }
  };

  return (
    <div className={`modal is-active ${isWaitingForMapClick ? 'modal-waiting-map-click' : ''}`}>
      <div className="modal-background"></div>
      <div className="modal-card mx-2 is-max-desktop">
        <header className="modal-card-head">
          <p className="modal-card-title">{isEditMode ? 'Edit Node' : 'Add Node to Map'}</p>
          <button className="delete" aria-label="close" onClick={onClose}></button>
        </header>
        
        <section className="modal-card-body">
          {!isEditMode && (
            <div className="field">
              <label className="label">Node ID (Hex String)</label>
            <div className="control">
              <textarea
                className="textarea is-family-monospace"
                value={nodeId}
                onChange={(e) => setNodeId(e.target.value)}
                placeholder="Paste meshcore:// URL or hex string here..."
                rows={4}
                disabled={submitting}
              />
            </div>
          </div>
          )}

          {!isEditMode && (
            <div className="field">
              <div className="control">
                <button 
                  className={`button is-primary is-fullwidth ${decoding ? 'is-loading' : ''}`}
                  onClick={handleDecode}
                  disabled={!nodeId || decoding || submitting}
                >
                  Decode Node ID
                </button>
              </div>
            </div>
          )}

          {error && (
            <article className={`message ${isWaitingForMapClick ? 'is-info' : 'is-warning'}`}>
              <div className="message-body">
                {error}
              </div>
            </article>
          )}

          {decodedInfo && (
            <div className="box">
              <h3 className="title is-5 has-text-primary mb-4">
                <span className="icon-text">
                  <span className="icon">
                    <i className="fas fa-edit"></i>
                  </span>
                  <span>Node Information (Editable)</span>
                </span>
              </h3>
              
              <div className="field">
                <label className="label has-text-dark">Friendly Name</label>
                <div className="control">
                  <input
                    className="input"
                    type="text"
                    value={editableFriendlyName}
                    onChange={(e) => setEditableFriendlyName(e.target.value)}
                    placeholder="Node name (optional)"
                    disabled={submitting}
                  />
                </div>
                <p className="help">Give your node a memorable name</p>
              </div>

              <div className="field">
                <label className="label has-text-dark">Node Hash</label>
                <div className="control">
                  <input
                    className="input is-family-monospace"
                    type="text"
                    value={editableNodeHash}
                    onChange={(e) => setEditableNodeHash(e.target.value)}
                    placeholder="0x00"
                    disabled={submitting}
                  />
                </div>
                <p className="help">Unique identifier for this node</p>
              </div>

              <div className="field">
                <label className="label has-text-dark">Public Key</label>
                <div className="control">
                  <input
                    className="input is-family-monospace"
                    type="text"
                    value={editablePublicKey}
                    onChange={(e) => setEditablePublicKey(e.target.value)}
                    placeholder="Public key (optional)"
                    disabled={submitting}
                  />
                </div>
                <p className="help">Node's public cryptographic key</p>
              </div>

              <div className="field">
                <label className="label has-text-dark">Node Type</label>
                <div className="control">
                  <label className="checkbox mr-4">
                    <input
                      type="checkbox"
                      checked={isRepeater}
                      onChange={(e) => setIsRepeater(e.target.checked)}
                      disabled={submitting}
                      className="mr-2"
                    />
                    Repeater
                  </label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={isRoomServer}
                      onChange={(e) => setIsRoomServer(e.target.checked)}
                      disabled={submitting}
                      className="mr-2"
                    />
                    Room Server
                  </label>
                </div>
                <p className="help">
                  Repeaters extend network coverage. Room Servers also store messages.
                  {(isRepeater || isRoomServer) && " Signal propagation will be calculated for this node."}
                </p>
              </div>

              {(isRepeater || isRoomServer) && (
                <>
                  <div className="columns">
                    <div className="column">
                      <div className="field">
                        <label className="label has-text-dark">Antenna Height (meters)</label>
                        <div className="control">
                          <input
                            className="input"
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={antennaHeight}
                            onChange={(e) => setAntennaHeight(e.target.value)}
                            disabled={submitting}
                          />
                        </div>
                        <p className="help">Height above ground level</p>
                      </div>
                    </div>
                    <div className="column">
                      <div className="field">
                        <label className="label has-text-dark">Antenna Gain (dBi)</label>
                        <div className="control">
                          <input
                            className="input"
                            type="number"
                            step="0.1"
                            min="-10"
                            max="20"
                            value={antennaGain}
                            onChange={(e) => setAntennaGain(e.target.value)}
                            disabled={submitting}
                          />
                        </div>
                        <p className="help">Antenna gain in dBi</p>
                      </div>
                    </div>
                  </div>
                  <div className="notification is-info is-light">
                    <p className="is-size-7">
                      <strong>LoRa Settings:</strong> 908.205 MHz, 62.5 kHz BW, SF11, CR7, 1W TX
                    </p>
                  </div>
                </>
              )}

              <div className="columns">
                <div className="column">
                  <div className="field">
                    <label className="label has-text-dark">Latitude</label>
                    <div className="control">
                      <input
                        className="input"
                        type="number"
                        step="0.000001"
                        value={editableLatitude}
                        onChange={(e) => setEditableLatitude(e.target.value)}
                        placeholder="34.048900"
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </div>

                <div className="column">
                  <div className="field">
                    <label className="label has-text-dark">Longitude</label>
                    <div className="control">
                      <input
                        className="input"
                        type="number"
                        step="0.000001"
                        value={editableLongitude}
                        onChange={(e) => setEditableLongitude(e.target.value)}
                        placeholder="-111.093700"
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="field">
                <div className="control">
                  <button 
                    className={`button is-info is-fullwidth ${isWaitingForMapClick ? 'is-loading' : ''}`}
                    onClick={handleMapClick}
                    disabled={submitting || isWaitingForMapClick}
                  >
                    <span className="icon">
                      <i className="fas fa-map-marker-alt"></i>
                    </span>
                    <span>
                      {isWaitingForMapClick ? 'Click on Map...' : 'Click Map to Set Location'}
                    </span>
                  </button>
                </div>
                <p className="help">Click anywhere on the map to set the node's location</p>
              </div>

              {decodedInfo.timestamp && (
                <div className="notification is-info is-light">
                  <div className="content">
                    <p>
                      <strong className="has-text-dark">Timestamp:</strong> 
                      <span className="has-text-dark ml-2">{decodedInfo.timestamp}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <footer className="modal-card-foot is-justify-content-flex-end">
          <button 
            className="button" 
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button 
            className={`button is-success ${submitting ? 'is-loading' : ''}`}
            onClick={handleSubmit}
            disabled={!decodedInfo || !editableLatitude || !editableLongitude || submitting}
          >
            {isEditMode ? 'Update Node' : 'Add to Map'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AddNodeModal;
